import { useCallback } from 'react';
import Papa from 'papaparse';

/**
 * Shared spreadsheet parsing for the import modals (ImportCSVModal and the
 * admin Import Studio). Extracted verbatim from ImportCSVModal — parsing
 * behavior must stay identical for both entry points.
 *
 * @param {Object} handlers
 * @param {(rows, headers, formatLabel) => void} handlers.onData
 *   Called with parsed rows for a CSV file or a single-sheet workbook.
 *   `formatLabel` is human text for toasts ("CSV", "Excel", 'Excel sheet "X"').
 * @param {(sheets: Array<{name, rows, headers}>) => void} handlers.onSheets
 *   Called when a workbook has multiple non-empty sheets (user must pick one).
 * @param {(toastConfig) => void} handlers.onError
 *   Called with a Chakra toast config for parse failures / empty workbooks.
 */
export default function useSpreadsheetParser({ onData, onSheets, onError }) {
  const parseCsvFile = useCallback((file) => {
    Papa.parse(file, {
      complete: (results) => {
        if (results.data && results.data.length > 0) {
          const headers = results.meta.fields || Object.keys(results.data[0]);
          onData(results.data, headers, 'CSV');
        } else {
          onData([], [], 'CSV');
        }
      },
      header: true,
      skipEmptyLines: true,
      error: (error) => {
        console.error('CSV parsing error:', error);
        onError({
          title: 'Error parsing CSV',
          description: error.message,
          status: 'error',
          duration: 5000,
        });
      },
    });
  }, [onData, onError]);

  const parseExcelFile = useCallback(async (file) => {
    try {
      // Loaded on demand so the spreadsheet parser stays out of the main bundle
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' });

      const sheets = [];
      workbook.SheetNames.forEach(name => {
        const sheet = workbook.Sheets[name];
        if (!sheet) return;
        // raw:false returns formatted display strings, which keeps Excel
        // quirks out of the pipeline: dates render as displayed (not serial
        // numbers), ZIPs keep leading zeros, currency stays "$250,000" (the
        // backend parses that safely).
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
        if (!rows.length) return;
        const headerRow = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false })[0] || [];
        // Keys of the row objects are the exact header cell text; blank header
        // cells become __EMPTY columns, which we drop.
        const headers = headerRow
          .map(h => String(h ?? ''))
          .filter(h => h.trim() && !h.startsWith('__EMPTY'));
        if (!headers.length) return;
        const hasData = rows.some(row => headers.some(h => String(row[h] ?? '').trim() !== ''));
        if (!hasData) return;
        sheets.push({ name, rows, headers });
      });

      if (sheets.length === 0) {
        onError({
          title: 'No data found',
          description: 'None of the sheets in this workbook contain data rows with headers.',
          status: 'warning',
          duration: 5000,
        });
        return;
      }

      if (sheets.length === 1) {
        onData(sheets[0].rows, sheets[0].headers, 'Excel');
        return;
      }

      // Multiple non-empty sheets: let the caller offer a picker
      onSheets(sheets);
    } catch (error) {
      console.error('Excel parsing error:', error);
      onError({
        title: 'Error parsing Excel file',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  }, [onData, onSheets, onError]);

  /** Dispatch a File to the CSV or Excel parser based on its extension. */
  const parseFile = useCallback((file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      parseExcelFile(file);
    } else {
      parseCsvFile(file);
    }
  }, [parseExcelFile, parseCsvFile]);

  return { parseFile, parseCsvFile, parseExcelFile };
}
